import React, { useEffect, useRef } from 'react';

interface Star {
  x: number;
  y: number;
  size: number;
  opacity: number;
  speed: number;
  color: string;
  layer: 1 | 2 | 3;
}

interface Rocket {
  x: number;
  y: number;
  speed: number;
  trail: { x: number; y: number; opacity: number }[];
}

interface ShootingStar {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  trail: { x: number; y: number }[];
}

interface Nebula {
  x: number;
  y: number;
  radius: number;
  color: string;
}

const STAR_COLORS = [
  'rgba(255, 255, 255, 1)',
  'rgba(240, 248, 255, 1)',
  'rgba(230, 230, 250, 1)',
  'rgba(176, 224, 230, 1)',
  'rgba(255, 223, 186, 1)',
];

const NEBULA_COLORS = [
  'rgba(139, 92, 246, 0.18)',
  'rgba(99, 102, 241, 0.16)',
  'rgba(236, 72, 153, 0.14)',
  'rgba(59, 130, 246, 0.16)',
];

const Starfield: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotionQuery =
      typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
        : undefined;
    let reducedMotion = reducedMotionQuery?.matches ?? false;

    let stars: Star[] = [];
    let rockets: Rocket[] = [];
    let shootingStars: ShootingStar[] = [];
    let nebulae: Nebula[] = [];
    let lastShootingStarSpawn = 0;
    let frame = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
      initRockets();
      initNebulae();
      if (reducedMotion) {
        renderStatic();
      }
    };

    function initStars() {
      const density = Math.floor((canvas.width * canvas.height) / 1100);
      const starCount = Math.min(density, 400);
      stars = [];

      for (let i = 0; i < starCount; i++) {
        const layerRoll = Math.random();
        const layer: 1 | 2 | 3 = layerRoll > 0.95 ? 3 : layerRoll > 0.7 ? 2 : 1;
        const sizeMultiplier = layer === 3 ? 2.2 : layer === 2 ? 1.3 : 0.7;
        const speedMultiplier = layer === 3 ? 2.5 : layer === 2 ? 1.4 : 0.6;

        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: (Math.random() * 1.5 + 0.5) * sizeMultiplier,
          opacity: Math.random() * 0.8 + 0.2,
          speed: (Math.random() * 0.1 + 0.01) * speedMultiplier,
          color: STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)],
          layer,
        });
      }
    }

    function initRockets() {
      rockets = [
        {
          x: Math.random() * canvas.width,
          y: canvas.height + 100,
          speed: Math.random() * 0.5 + 0.3,
          trail: [],
        },
      ];
    }

    function initNebulae() {
      const count = 4;
      nebulae = [];
      for (let i = 0; i < count; i++) {
        nebulae.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.min(canvas.width, canvas.height) * (0.25 + Math.random() * 0.2),
          color: NEBULA_COLORS[i % NEBULA_COLORS.length],
        });
      }
    }

    function spawnShootingStar() {
      const fromTop = Math.random() > 0.5;
      const startX = Math.random() * canvas.width * 0.6;
      const startY = fromTop ? -20 : Math.random() * canvas.height * 0.4;
      const angle = Math.PI / 4 + (Math.random() - 0.5) * 0.3;
      const speed = 12 + Math.random() * 6;
      shootingStars.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        trail: [],
      });
    }

    function drawNebulae() {
      nebulae.forEach((n) => {
        const gradient = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.radius);
        gradient.addColorStop(0, n.color);
        gradient.addColorStop(1, n.color.replace(/[\d.]+\)$/, '0)'));
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function drawStar(star: Star) {
      const gradient = ctx.createRadialGradient(
        star.x,
        star.y,
        0,
        star.x,
        star.y,
        star.size * 2
      );
      gradient.addColorStop(0, star.color.replace('1)', `${star.opacity})`));
      gradient.addColorStop(1, star.color.replace('1)', '0)'));

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      ctx.fill();

      // Cross-ray sparkle for brightest layer
      if (star.layer === 3) {
        ctx.strokeStyle = star.color.replace('1)', `${star.opacity * 0.6})`);
        ctx.lineWidth = 0.6;
        const rayLen = star.size * 4;
        ctx.beginPath();
        ctx.moveTo(star.x - rayLen, star.y);
        ctx.lineTo(star.x + rayLen, star.y);
        ctx.moveTo(star.x, star.y - rayLen);
        ctx.lineTo(star.x, star.y + rayLen);
        ctx.stroke();
      }
    }

    function drawRocket(rocket: Rocket) {
      rocket.trail.push({ x: rocket.x, y: rocket.y, opacity: 1 });
      if (rocket.trail.length > 15) rocket.trail.shift();

      rocket.trail.forEach((point, index) => {
        const alpha = (index / rocket.trail.length) * point.opacity * 0.5;
        ctx.fillStyle = `rgba(255, 150, 0, ${alpha})`;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 2, 0, Math.PI * 2);
        ctx.fill();
        point.opacity *= 0.98;
      });

      ctx.font = '20px sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillText('🚀', rocket.x - 10, rocket.y + 5);

      rocket.y -= rocket.speed;
      rocket.x += (Math.random() - 0.5) * 0.2;

      if (rocket.y < -50) {
        rocket.y = canvas.height + 100;
        rocket.x = Math.random() * canvas.width;
        rocket.trail = [];
      }
    }

    function drawShootingStar(s: ShootingStar) {
      s.trail.push({ x: s.x, y: s.y });
      if (s.trail.length > 25) s.trail.shift();

      // Draw tail
      s.trail.forEach((p, i) => {
        const alpha = (i / s.trail.length) * s.life;
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.8})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5 * (i / s.trail.length) + 0.5, 0, Math.PI * 2);
        ctx.fill();
      });

      // Head
      const headGradient = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, 6);
      headGradient.addColorStop(0, `rgba(255, 255, 255, ${s.life})`);
      headGradient.addColorStop(1, `rgba(186, 230, 253, 0)`);
      ctx.fillStyle = headGradient;
      ctx.beginPath();
      ctx.arc(s.x, s.y, 6, 0, Math.PI * 2);
      ctx.fill();

      s.x += s.vx;
      s.y += s.vy;
      if (s.x > canvas.width + 50 || s.y > canvas.height + 50) {
        s.life = 0;
      }
    }

    function renderStatic() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawNebulae();
      stars.forEach(drawStar);
    }

    function animate() {
      if (reducedMotion) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      drawNebulae();

      stars.forEach((star) => {
        drawStar(star);
        star.y = (star.y + star.speed) % canvas.height;
        star.opacity = Math.max(
          0.2,
          Math.min(1, star.opacity + (Math.random() - 0.5) * 0.02)
        );
      });

      rockets.forEach(drawRocket);

      // Spawn a shooting star roughly every 6s (assuming ~60fps → 360 frames)
      frame += 1;
      if (frame - lastShootingStarSpawn > 360 && shootingStars.length === 0) {
        spawnShootingStar();
        lastShootingStarSpawn = frame;
      }

      shootingStars = shootingStars.filter((s) => s.life > 0);
      shootingStars.forEach(drawShootingStar);

      animationFrameId.current = requestAnimationFrame(animate);
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      cancelAnimationFrame(animationFrameId.current);

      if (reducedMotion) {
        renderStatic();
      } else {
        animate();
      }
    };

    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    if (reducedMotion) {
      renderStatic();
    } else {
      animate();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none z-0"
      style={{ backgroundColor: 'transparent' }}
      data-testid="starfield-canvas"
    />
  );
};

export default Starfield;
